import Button from '@material-ui/core/Button'
import Grid from '@material-ui/core/Grid'
import Alert from '@material-ui/lab/Alert'
import { DeepPartial, unwrapResult } from '@reduxjs/toolkit'
import FormikTextField from 'components/Fields/FormikTextField'
import { Form, Formik } from 'formik'
import { useSnackbar } from 'notistack'
import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { apiStatus, StarLanguage } from 'shared/constants/AppEnum'
import { useAppDispatch } from 'store/hooks'
import { createTempleThunk, templeUpdateThunk } from 'store/reducers/temple/templeSlice'
import { ITempleResponse } from 'types/api/temple'
import { IApiState } from 'store/types'
import { extractError, getEditedPayload, getFieldErrors, isFieldError } from 'utils/api'
import * as yup from 'yup'
import { FormikPhoneField } from 'components/Fields/FormikPhoneField'
import TempleLocation from './TempleLocation'
import { useState } from 'react'
import { Box, Checkbox, Divider, FormControlLabel, Typography } from '@material-ui/core'
import { countryCodeRegex, phoneNoRegex } from 'shared/constants/AppConst'
import { KeyboardTimePicker } from '@material-ui/pickers'
import { ITempleFormType } from 'types/store/temple'
import { dateToUTCTimeString, timeStringToUTCDate } from 'utils/fns'
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab'


interface TempleFormProps {
    temple?: ITempleResponse
    showMap?: boolean
    api: IApiState
    buttonLabelId?: string
    successCb?: () => void
}

const getInitialValues = (temple?: ITempleResponse): ITempleFormType => {
    return {
        name: temple?.name ?? '',
        counterName: temple?.counterName ?? "",
        phone: temple?.phone ?? '',
        email: temple?.email ?? '',
        contact_no_1: temple?.contact_no_1 ?? '',
        contact_no_2: temple?.contact_no_2 ?? '',
        openingTime: temple?.openingTime ? timeStringToUTCDate(temple.openingTime, 1) : '',
        closingTime: temple?.closingTime ? timeStringToUTCDate(temple.closingTime) : '',
        offlineConsolidationTime: temple?.offlineConsolidationTime ? timeStringToUTCDate(temple.offlineConsolidationTime) : '',
        enableDakshina: temple?.enableDakshina ?? false,
        starLang: temple?.starLang ?? StarLanguage.MALAYALAM,
        address: {
            address_line_1: temple?.address?.address_line_1 ?? '',
            address_line_2: temple?.address?.address_line_2 ?? '',
            city: temple?.address?.city ?? '',
            district: temple?.address?.district ?? '',
            state: temple?.address?.state ?? '',
            pincode: temple?.address?.pincode ?? ''
        },
        description: temple?.description ?? '',
        whatsappNo: temple?.whatsappNo ?? '',
        dakshinaInConsolidation: temple?.dakshinaInConsolidation ?? false
        ,
        calendar: temple?.calendar ?? 'koz',
        defaultGateway: temple?.defaultGateway ?? 'RP',
        defaultCourierCharge: temple?.defaultCourierCharge ?? '',
        enableTbaBooking: temple?.enableTbaBooking ?? false,
        tbaUpfrontBookingPercentage: temple?.tbaUpfrontBookingPercentage ?? 100,
        allowZeroAmountTransactions: temple?.allowZeroAmountTransactions ?? false
    }
}

const formToTemple = (values: ITempleFormType): DeepPartial<ITempleResponse> => {
    return {
        ...values,
        whatsappNo: countryCodeRegex.test(values.whatsappNo ?? "") ? '' : values.whatsappNo,
        contact_no_1: countryCodeRegex.test(values.contact_no_1 ?? "") || values.contact_no_1 === '' ? null : values.contact_no_1,
        contact_no_2: countryCodeRegex.test(values.contact_no_2 ?? "") || values.contact_no_2 === '' ? null : values.contact_no_2,
        openingTime: values.openingTime ? dateToUTCTimeString(values.openingTime) : null,
        closingTime: values.closingTime ? dateToUTCTimeString(values.closingTime) : null,
        offlineConsolidationTime: values.offlineConsolidationTime ? dateToUTCTimeString(values.offlineConsolidationTime) : null,
        calendar: values.calendar,
        defaultGateway: values.defaultGateway,
        defaultCourierCharge: values.defaultCourierCharge === '' ? null : values.defaultCourierCharge,
        enableTbaBooking: values.enableTbaBooking,
        tbaUpfrontBookingPercentage: values.tbaUpfrontBookingPercentage,
        allowZeroAmountTransactions: values.allowZeroAmountTransactions
    }
}

const TempleForm: React.FC<TempleFormProps> =
    ({ temple, api, showMap = false, buttonLabelId = 'common.save', successCb }) => {
        const { messages } = useIntl()
        const initialValues = useMemo(() => getInitialValues(temple), [temple])
        const dispatch = useAppDispatch()
        const { enqueueSnackbar } = useSnackbar()
        const [latlng, setLatLng] = useState<{ lat: number, lng: number }>()
        const [latlngErr, setLatLngErr] = useState('')

        const mapCb = (lat: number, lng: number) => {
            setLatLngErr('')
            setLatLng({ lat: lat, lng: lng })
        }

        const validationSchema = yup.object({
            name: yup.string().required(),
            counterName: yup.string(),
            email: yup.string().required().email(),
            address: yup.object({
                address_line_1: yup.string().required(),
                address_line_2: yup.string(),
                city: yup.string().required(),
                state: yup.string().required(),
                pincode: yup.string()
                    .required()
                    .length(6, 'Only 6 digits are allowed')
                    .test('is-digits', 'Pincode should have digits only', (value) => /^\d+$/.test(value ?? ''))
            }),
            phone: yup.string()
                .test('invalid-phone-no', 'Invalid phone number', (value) => phoneNoRegex.test(value ?? '')),
            contact_no_1: yup.string()
                .test('invalid-phone-no', 'Invalid phone number', (value) => value && value !== '' ? phoneNoRegex.test(value) || countryCodeRegex.test(value) : true),
            contact_no_2: yup.string()
                .test('invalid-phone-no', 'Invalid phone number', (value) => value ? phoneNoRegex.test(value) || countryCodeRegex.test(value) : true),
            whatsappNo: yup.string()
                .test('invalid-phone-no', 'Invalid phone number', (value) => value && value !== '' ? phoneNoRegex.test(value) || countryCodeRegex.test(value) : true),
            openingTime: yup.date(),
            closingTime: yup.date().min(yup.ref('openingTime'), 'Closing time can\'t be before starting time'),
            description: yup.string(),
            dakshinaInConsolidation: yup.boolean(),
            enableDakshina: yup.boolean(),
            starLang: yup.mixed<StarLanguage>().oneOf(Object.values(StarLanguage)),
            calendar: yup.string().oneOf(['koz', 'tvm']),
            defaultGateway: yup.mixed().oneOf(['RP', 'EB', 'CL']),
            defaultCourierCharge: yup
                .number()
                .transform((value, originalValue) => originalValue === '' || originalValue === null ? undefined : value)
                .typeError('Default courier charge must be a number')
                .min(0, 'Default courier charge cannot be negative')
                .nullable(),
            enableTbaBooking: yup.boolean(),
            tbaUpfrontBookingPercentage: yup
                .number()
                .typeError('TBA upfront percentage must be a number')
                .min(0, 'TBA upfront percentage cannot be negative')
                .max(100, 'TBA upfront percentage cannot be more than 100')
                .nullable(),
            allowZeroAmountTransactions: yup.boolean()
        })


        return (
            <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={(values, actions) => {
                    if (temple?.url) {
                        // Ensure full address object is sent if any address sub-field changed
                        let data = getEditedPayload(formToTemple(initialValues), formToTemple(values), [], ['address'])
                        dispatch(templeUpdateThunk({ url: temple.url, data: data }))
                            .then(unwrapResult)
                            .then(() => {
                                enqueueSnackbar('Temple Updated', { variant: 'success' })
                                actions.resetForm()
                            })
                            .catch(err => {
                                Object.entries(getFieldErrors(data, err)).forEach(([key, value]) =>
                                    actions.setFieldError(key, value))
                            })
                            .finally(() => {
                                actions.setSubmitting(false)
                            })
                    } else {
                        if (!latlng) {
                            setLatLngErr('Location is required')
                            actions.setSubmitting(false)
                        }
                        else dispatch(createTempleThunk({
                            ...formToTemple(values),
                            ...{ location: { latlng: { latitude: latlng.lat, longitude: latlng.lng } } }
                        } as ITempleResponse))
                            .then(unwrapResult)
                            .then(() => {
                                if (successCb) successCb()
                                actions.resetForm()
                            })
                            .catch(err => {
                                Object.entries(getFieldErrors(values, err)).forEach(([key, value]) =>
                                    actions.setFieldError(key, value))
                            })
                            .finally(() => {
                                actions.setSubmitting(false)
                            })
                    }
                }}
            >
                {({ isValid, dirty, isSubmitting, setFieldValue, values, errors }) => (
                    <Form noValidate autoComplete='off'>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <FormikTextField
                                    name='name'
                                    variant='outlined'
                                    required
                                    fullWidth
                                    placeholder={messages['temple.name']}
                                    label={messages['temple.name']}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormikTextField
                                    name='counterName'
                                    variant='outlined'
                                    fullWidth

                                    placeholder={messages['temple.counterName']}
                                    label={messages['temple.counterName']}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormikTextField
                                    name='email'
                                    variant='outlined'
                                    required
                                    fullWidth
                                    placeholder={messages['common.email']}
                                    label={messages['common.email']}
                                />
                            </Grid>
                            <Grid item md={6} xs={12}>
                                <KeyboardTimePicker
                                    label={messages['temple.openingTime']}
                                    onChange={time => setFieldValue('openingTime', time ?? '')}
                                    value={values.openingTime === '' ? null : values.openingTime}
                                    // format="hh:mm aa"
                                    mask="__:__ _M"
                                    inputVariant="outlined"
                                    size="small"
                                    placeholder="hh:mm AM/PM"
                                    error={errors.openingTime !== undefined}
                                    helperText={errors.openingTime}
                                    margin="normal"
                                    fullWidth
                                />
                            </Grid>
                            <Grid item md={6} xs={12}>
                                <KeyboardTimePicker
                                    label={messages['temple.closingTime']}
                                    onChange={time => setFieldValue('closingTime', time ?? '')}
                                    value={values.closingTime === '' ? null : values.closingTime}
                                    // format="hh:mm aa"
                                    mask="__:__ _M"
                                    inputVariant="outlined"
                                    size="small"
                                    placeholder="hh:mm AM/PM"
                                    error={errors.closingTime !== undefined}
                                    helperText={errors.closingTime}
                                    margin="normal"
                                    fullWidth
                                />
                            </Grid>


                            <Grid item xs={12}>
                                <FormikTextField
                                    name='address.address_line_1'
                                    variant='outlined'
                                    required
                                    fullWidth
                                    placeholder={messages['address.addressLine1']}
                                    label={messages['address.addressLine1']}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormikTextField
                                    name='address.address_line_2'
                                    variant='outlined'
                                    fullWidth
                                    placeholder={messages['address.addressLine2']}
                                    label={messages['address.addressLine2']}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <FormikTextField
                                    name='address.city'
                                    variant='outlined'
                                    required
                                    fullWidth
                                    placeholder={messages['address.city']}
                                    label={messages['address.city']}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <FormikTextField
                                    name='address.state'
                                    variant='outlined'
                                    required
                                    fullWidth
                                    placeholder={messages['address.state']}
                                    label={messages['address.state']}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <FormikTextField
                                    name='address.district'
                                    variant='outlined'
                                    required
                                    fullWidth
                                    placeholder={messages['address.district']}
                                    label={messages['address.district']}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <FormikTextField
                                    name='address.pincode'
                                    variant='outlined'
                                    required
                                    fullWidth
                                    placeholder={messages['address.pincode']}
                                    label={messages['address.pincode']}
                                    type="number"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormikPhoneField size='small'
                                    name="phone"
                                    required={true}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormikPhoneField size='small'
                                    name="contact_no_1"
                                    label="Contact No. 1"
                                    required={false}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormikPhoneField size='small'
                                    name="contact_no_2"
                                    label="Contact No. 2"
                                    required={false}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormikPhoneField size='small'
                                    name="whatsappNo"
                                    label="Whatsapp No."
                                    required={false}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <KeyboardTimePicker
                                    label={messages['temple.offlineConsolidationTime']}
                                    onChange={time => setFieldValue('offlineConsolidationTime', time ?? '')}
                                    value={values.offlineConsolidationTime === '' ? null : values.offlineConsolidationTime}
                                    // format="hh:mm aa"
                                    mask="__:__ _M"
                                    inputVariant="outlined"
                                    size="small"
                                    placeholder="hh:mm AM/PM"
                                    error={errors.offlineConsolidationTime !== undefined}
                                    helperText={errors.offlineConsolidationTime}
                                    margin="normal"
                                    fullWidth
                                />
                            </Grid>
                            {/* <Grid item xs={12} md={6} style={{ marginTop: 2 }}>
                                <FormControlLabel control={<Checkbox
                                    onChange={(e) => { setFieldValue('enableDakshina', e.target.checked) }}
                                    checked={values.enableDakshina}
                                    color='primary'
                                />} label="Enable Dakshina" />
                            </Grid> */}

                            <Grid item xs={12} md={12} style={{ marginTop: 2 }}>
                                <FormControlLabel control={<Checkbox
                                    onChange={(e) => { setFieldValue('dakshinaInConsolidation', e.target.checked) }}
                                    checked={values.dakshinaInConsolidation}
                                    color='primary'
                                />} label="Enable Dakshina in Consolidation Reports" />
                            </Grid>

                            <Grid item xs={12} md={12} style={{ marginTop: 2 }}>
                                <FormControlLabel control={<Checkbox
                                    onChange={(e) => { setFieldValue('allowZeroAmountTransactions', e.target.checked) }}
                                    checked={values.allowZeroAmountTransactions}
                                    color='primary'
                                />} label="Allow Zero Amount Transactions" />
                            </Grid>


                            <Grid item xs={6} style={{ marginTop: 2 }}>
                                <Typography style={{ marginBottom: 2 }}>Select Star Language</Typography>
                                <ToggleButtonGroup size='small' value={values.starLang} >
                                    {
                                        Object.entries(StarLanguage).map(([lang, val], i) => (
                                            <ToggleButton onClick={() => {
                                                setFieldValue('starLang', val)
                                            }} key={`star_lang_${i}`} value={val}>{lang}</ToggleButton>
                                        ))
                                    }
                                </ToggleButtonGroup>
                            </Grid>

                            <Grid item xs={6} style={{ marginTop: 2 }}>
                                <Typography style={{ marginBottom: 2 }}>Select Calendar</Typography>
                                <ToggleButtonGroup size='small' value={values.calendar}>
                                    <ToggleButton onClick={() => setFieldValue('calendar', 'koz')} value={'koz'}>Malabar</ToggleButton>
                                    <ToggleButton onClick={() => setFieldValue('calendar', 'tvm')} value={'tvm'}>Travancore</ToggleButton>
                                </ToggleButtonGroup>
                            </Grid>

                            <Grid item xs={6} style={{ marginTop: 2 }}>
                                <Typography style={{ marginBottom: 2 }}>Default Gateway</Typography>
                                <ToggleButtonGroup size='small' value={values.defaultGateway}>
                                    <ToggleButton onClick={() => setFieldValue('defaultGateway', 'RP')} value={'RP'}>Razorpay</ToggleButton>
                                    <ToggleButton onClick={() => setFieldValue('defaultGateway', 'EB')} value={'EB'}>Easebuzz</ToggleButton>
                                    <ToggleButton onClick={() => setFieldValue('defaultGateway', 'CL')} value={'CL'}>Clover</ToggleButton>
                                </ToggleButtonGroup>
                            </Grid>

                            <Grid item xs={6}>
                                <FormikTextField
                                    name='defaultCourierCharge'
                                    variant='outlined'
                                    fullWidth
                                    type='number'
                                    inputProps={{ min: 0, step: 'any' }}
                                    placeholder='Default courier charge'
                                    label='Default courier charge'
                                />
                            </Grid>

                            <Grid item xs={6} style={{ marginTop: 2 }}>
                                <FormControlLabel control={<Checkbox
                                    onChange={(e) => {
                                        setFieldValue('enableTbaBooking', e.target.checked)
                                    }}
                                    checked={values.enableTbaBooking}
                                    color='primary'
                                />} label="Enable TBA Booking" />
                            </Grid>

                            <Grid item xs={6}>
                                <FormikTextField
                                    name='tbaUpfrontBookingPercentage'
                                    variant='outlined'
                                    fullWidth
                                    type='number'
                                    inputProps={{ min: 0, max: 100, step: 'any' }}
                                    placeholder='TBA upfront percentage'
                                    label='TBA upfront percentage'
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <FormikTextField
                                    name="description"
                                    label="About Temple"
                                    variant='outlined'
                                    fullWidth
                                    multiline
                                    rowsMax={10}
                                />
                            </Grid>

                            {showMap && (
                                <Grid item xs={12}>
                                    <Box mb={6} mt={3}>
                                        <TempleLocation cb={mapCb} />
                                        {latlngErr && (
                                            <Alert style={{ marginTop: '20px' }} severity="error">{latlngErr}</Alert>
                                        )}
                                    </Box>
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                <Box alignContent="flex-end">
                                    <Button
                                        style={{ float: 'right' }}
                                        disabled={isSubmitting || !isValid || !dirty}
                                        variant="contained"
                                        color="primary"
                                        type="submit"
                                    >
                                        {messages[buttonLabelId]}
                                    </Button>
                                </Box>
                            </Grid>
                            {api.status === apiStatus.FAILED && !isFieldError(api.error) && (
                                <Grid item xs={12}>
                                    <Alert severity="error">{extractError(api.error)}</Alert>
                                </Grid>
                            )}
                        </Grid>
                    </Form>
                )}
            </Formik>
        )
    }

export default TempleForm
